import React, { useState, useEffect } from 'react';
import api from '../utils/axiosConfig';
import './UserManagement.css';
import './TableStyles.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'Operador'
  });
  const [passwordChange, setPasswordChange] = useState({
    userId: '',
    newPassword: ''
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/users');
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError('Erro ao carregar usuários: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await api.post('/auth/register', newUser);
      setSuccess('Usuário adicionado com sucesso!');
      setNewUser({ username: '', email: '', password: '', role: 'Operador' });
      setShowAddForm(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao adicionar usuário: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await api.put(`/auth/change-password/${passwordChange.userId}`, { 
        newPassword: passwordChange.newPassword 
      });
      setSuccess('Senha alterada com sucesso!');
      setPasswordChange({ userId: '', newPassword: '' });
      setShowChangePasswordForm(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao alterar senha: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/auth/users/${userId}`);
      setSuccess('Usuário excluído com sucesso!');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao excluir usuário: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (user) => {
    if (selectedUser && selectedUser._id === user._id) {
      setSelectedUser(null);
    } else {
      setSelectedUser(user);
    }
  };

  return (
    <div className="user-management-container">
      <h2>👥 Gerenciamento de Usuários</h2>
      
      {success && <div className="success-message">{success}</div>}
      {error && <div className="error-message">{error}</div>}
      
      <div className="user-actions">
        <button 
          className="add-user-button"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancelar' : '➕ Adicionar Usuário'}
        </button>
        <button 
          className="change-password-button"
          onClick={() => setShowChangePasswordForm(!showChangePasswordForm)}
        >
          {showChangePasswordForm ? 'Cancelar' : '🔐 Alterar Senha'}
        </button>
      </div>

      {showAddForm && (
        <form className="add-user-form" onSubmit={handleAddUser}>
          <h3>Adicionar Novo Usuário</h3>
          <div className="form-group">
            <label htmlFor="username">Nome de Usuário:</label>
            <input
              type="text"
              id="username"
              value={newUser.username}
              onChange={(e) => setNewUser({...newUser, username: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Senha:</label>
            <input
              type="password"
              id="password"
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="role">Função:</label>
            <select
              id="role"
              value={newUser.role}
              onChange={(e) => setNewUser({...newUser, role: e.target.value})}
            >
              <option value="Operador">Operador</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Administrador">Administrador</option>
            </select>
          </div>
          <div className="form-buttons">
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Adicionando...' : 'Adicionar Usuário'}
            </button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={() => setShowAddForm(false)}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {showChangePasswordForm && (
        <form className="add-user-form" onSubmit={handleChangePassword}>
          <h3>Alterar Senha de Usuário</h3>
          <div className="form-group">
            <label htmlFor="userId">Usuário:</label>
            <select
              id="userId"
              value={passwordChange.userId}
              onChange={(e) => setPasswordChange({...passwordChange, userId: e.target.value})}
              required
            >
              <option value="">Selecione um usuário</option>
              {users.map(user => (
                <option key={user._id} value={user._id}>
                  {user.username} ({user.role})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="newPassword">Nova Senha:</label>
            <input
              type="password"
              id="newPassword"
              value={passwordChange.newPassword}
              onChange={(e) => setPasswordChange({...passwordChange, newPassword: e.target.value})}
              required
            />
          </div>
          <div className="form-buttons">
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Alterando...' : 'Alterar Senha'}
            </button>
            <button 
              type="button" 
              className="cancel-button"
              onClick={() => setShowChangePasswordForm(false)}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="users-list">
        <h3>Lista de Usuários ({users.length})</h3>
        <div className="global-table-container">
          <table className="global-table">
            <thead>
              <tr>
                <th>Nome de Usuário</th>
                <th>Email</th>
                <th>Função</th>
                <th>Senha (Hash)</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>
                    <div className="password-display">
                      <button 
                        className="toggle-password-button"
                        onClick={() => togglePasswordVisibility(user)}
                      >
                        {selectedUser && selectedUser._id === user._id ? 'Ocultar' : 'Mostrar'}
                      </button>
                      {selectedUser && selectedUser._id === user._id && (
                        <div className="password-hash">
                          {user.password}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="user-actions">
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteUser(user._id)}
                        disabled={loading}
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;